import { env } from './env'
import { getInvestorSessionToken } from './investorSession'

export interface HttpErrorOptions {
  status?: number
  statusText?: string
  body?: string
}

export class HttpError extends Error {
  readonly status?: number
  readonly statusText?: string
  readonly body?: string

  constructor(message: string, { status, statusText, body }: HttpErrorOptions = {}) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.statusText = statusText
    this.body = body
  }
}

type ApiRequestOptions = {
  method?: string
  data?: unknown
  formData?: FormData
  signal?: AbortSignal
}

function apiUrl(path: string) {
  return `${env.apiBaseUrl}${path}`
}

function sessionHeaders(): Record<string, string> {
  const token = getInvestorSessionToken()
  return token ? { 'X-Investor-Session': token } : {}
}

async function readError(response: Response): Promise<HttpError> {
  const body = await response.text()
  let message = body || `${response.status} ${response.statusText}`.trim()
  try {
    const parsed = JSON.parse(body) as { message?: string; title?: string }
    message = parsed.message || parsed.title || message
  } catch {
    /* keep text body */
  }
  return new HttpError(message, {
    status: response.status,
    statusText: response.statusText,
    body,
  })
}

export async function apiRequest<TResponse>(path: string, options?: ApiRequestOptions): Promise<TResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...sessionHeaders(),
  }
  let body: BodyInit | undefined
  if (options?.formData) {
    body = options.formData
  } else if (options?.data !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.data)
  }

  const response = await fetch(apiUrl(path), {
    method: options?.method ?? 'GET',
    credentials: 'include',
    headers,
    body,
    signal: options?.signal,
  })

  if (!response.ok) {
    throw await readError(response)
  }

  if (response.status === 204) {
    return null as TResponse
  }

  const text = await response.text()
  if (!text) {
    return null as TResponse
  }

  return JSON.parse(text) as TResponse
}

export async function apiDownload(path: string, data: unknown, signal?: AbortSignal): Promise<void> {
  const response = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: '*/*', 'Content-Type': 'application/json', ...sessionHeaders() },
    body: JSON.stringify(data),
    signal,
  })

  if (!response.ok) {
    throw await readError(response)
  }

  const blob = await response.blob()
  const fileName = fileNameFromDisposition(response.headers.get('content-disposition')) || 'download.html'
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1].replaceAll('"', ''))
  }
  const match = /filename="?([^";]+)"?/i.exec(header)
  return match?.[1] ?? null
}
